import AuthGuard from "./AuthGuard";
import ParticipantsLayout from "./ParticipantsLayout";
import ProjectsLayout from "./ProjectsLayout";
import SettingsLayout from "./SettingsLayout";
import SubscriptionsLayout from "./SubscriptionsLayout";
import EmbedViewerLayout from "../components/embed-viewer/EmbedViewerLayout";
import { useParams, useLocation, useRoutes, Navigate } from "react-router-dom";
import {
  Products,
  NewTest,
  Error404,
  Template,
  Dashboard,
  Response,
  EndScreen,
  StartScreen,
  TestReport,
  TestPreview,
  TestCreation,
  BrowserTest,
  EmbedViewer,
  EmbedViewerTreeTask,
  EmbedViewerCardSort,
  EditProfile,
  Billings,
  Subscriptions,
  Integrations,
  GlobalSettings,
  UserOnboarding,
  People,
  Workspace,
  Organization,
  WorkspaceSwitch,
  WorkspaceInvitation,
  DashboardModerated,
  ModeratedEventEditor,
  ModeratedSlotBooking,
  AiInterviewLanding,
  Heatmap,
  Plans,
  UpgradePlan,
  TopUp,
  ToolkitDashboard,
  ToolkitTestCreation,
  ToolkitTestCreationInit,
  ToolkitReport,
  SimilarityMatrix,
  ToolkitTesterview,
  ToolkitReportPieTree,
  ToolkitReportDendrogram,
  NavigationTree,
  VideoPlayer,
  ModeratedVideoPlayer,
  ParticipantsList,
  Projects,
  ProjectDetail,
  AllStudies,
  PublicFilesView,
  TagDetail,
  ManageTagsPage,
} from "./elements";

const BrowserTestRedirect = () => {
  const { testId } = useParams();
  const { search } = useLocation();

  return <Navigate to={`/simply-feedback/${testId}${search}`} replace />;
};

export default function Router() {
  return useRoutes([
    {
      path: "/",
      element: <AuthGuard fetchBalances fetchProjects />,
      children: [
        { element: <Products />, index: true },
        { path: "dashboard", element: <Dashboard /> },
        { path: "surveys", element: <Dashboard isSurveys={true} /> },
        { path: "norecording", element: <Dashboard isNoRecordingFigma={true} /> },
        { path: "templates", element: <Template /> },
        { path: "template/:templateId", element: <Template /> },
        { path: "moderated", element: <DashboardModerated /> },
        { path: "moderated/schedules", element: <DashboardModerated isSchedules /> },
        { path: "moderated/scheduler", element: <DashboardModerated isScheduler /> },
        { path: "moderated/:createNewProject", element: <Navigate to='/moderated' /> },
        { path: "moderated/:projectId/edit", element: <Navigate to='/moderated' /> },
        { path: "moderated/:projectId/report", element: <DashboardModerated isReport /> },
        { path: "moderated/:projectId/report/videoplayer/:sessId", element: <ModeratedVideoPlayer /> },
        { path: "moderated/event/edit/:eventId", element: <ModeratedEventEditor /> },
        {
          path: "test",
          children: [
            { path: "new", element: <NewTest /> },
            { path: ":draftId", element: <TestCreation /> },
            { path: ":draftId/report/videoplayer/:userId/", element: <VideoPlayer /> },
            { path: ":draftId/report/videoplayer/:userId/:taskId", element: <VideoPlayer /> },
            { path: ":draftId/navtree/:taskId", element: <NavigationTree /> },
            { path: ":draftId/:activeTab", element: <TestCreation /> },
          ],
        },
        { path: "onboarding", element: <UserOnboarding /> },
        { path: "workspace/:id", element: <WorkspaceSwitch /> },
        { path: "invites/:resourceType/:resourceId/:uuid", element: <WorkspaceInvitation /> },
        // Toolkit routes under same shell to prevent GlobalAppShell remount on navigation
        {
          path: "toolkit",
          children: [
            { element: <ToolkitDashboard />, index: true },
            { path: ":menuType", element: <ToolkitDashboard /> },
          ],
        },
        {
          path: "tests",
          children: [
            { path: ":testId", element: <ToolkitTestCreation /> },
            { path: ":testId/:activeTab", element: <ToolkitTestCreation /> },
            { path: "new/:testType", element: <ToolkitTestCreationInit /> },
          ],
        },
        {
          path: "tests/report",
          children: [
            { path: ":testId", element: <ToolkitReport /> },
            { path: ":testId/dendrogram", element: <ToolkitReportDendrogram /> },
            { path: ":testId/similarity-matrix", element: <SimilarityMatrix /> },
            { path: ":testId/pie-tree/:activeTaskIndex", element: <ToolkitReportPieTree /> },
            { path: ":testId/pie-tree/:activeTaskIndex/:isNewPage", element: <ToolkitReportPieTree /> },
          ],
        },
        // Toolkit routes end
        // Participant management — nav link is always visible; ParticipantsList itself renders
        // a locked-feature promo for non-Organization plans instead of redirecting away.
        {
          element: <ParticipantsLayout />,
          children: [{ path: "participants", element: <ParticipantsList /> }],
        },
        // All Projects — the listing and a project's detail page share one layout, so the
        // header can name the project and step back up to the list. All Studies joins them:
        // it is the same chrome over the same rows, scoped to the workspace instead of one
        // project.
        {
          element: <ProjectsLayout />,
          children: [
            { path: "projects", element: <Projects /> },
            { path: "projects/:projectId", element: <ProjectDetail /> },
            { path: "studies", element: <AllStudies /> },
          ],
        },
        { path: "tags", element: <ManageTagsPage /> },
        { path: "tags/:tagId", element: <TagDetail /> },
      ],
    },
    {
      path: "plans",
      element: <SubscriptionsLayout />,
      children: [
        { element: <Plans />, index: true },
        { path: "upgrade/:planId/:recurringType", element: <UpgradePlan /> },
        { path: "topup", element: <TopUp /> },
      ],
    },
    {
      path: "response",
      children: [
        { element: <Response />, index: true },
        { path: "end", element: <EndScreen /> },
        { path: "start", element: <StartScreen /> },
        { path: "preview/:draftId", element: <Response /> },
        { path: ":setId", element: <Response /> },
        { path: ":setId/:userId", element: <Response /> },
      ],
    },
    {
      path: "user",
      element: <SettingsLayout />,
      children: [
        { path: "edit-profile", element: <EditProfile /> },
        { path: "billings", element: <Billings /> },
        { path: "credits-usage", element: <Subscriptions /> },
        { path: "integrations", element: <Integrations /> },
        { path: "global-settings", element: <GlobalSettings /> },
        { path: "organization", element: <Organization /> },
        { path: "organization/people", element: <People /> },
        { path: "organization/workspace", element: <Workspace /> },
      ],
    },
    // Toolkit routes start
    {
      path: "toolkit/share/report",
      children: [
        { path: ":testId", element: <ToolkitReport isSharedReport /> },
        { path: ":testId/dendrogram", element: <ToolkitReportDendrogram isSharedReport /> },
        { path: ":testId/similarity-matrix", element: <SimilarityMatrix isSharedReport /> },
        { path: ":testId/pie-tree/:activeTaskIndex", element: <ToolkitReportPieTree isSharedReport /> },
        { path: ":testId/pie-tree/:activeTaskIndex/:isNewPage", element: <ToolkitReportPieTree isSharedReport /> },
      ],
    },
    {
      path: "study",
      children: [
        { element: <ToolkitTesterview />, index: true },
        { path: ":testId", element: <ToolkitTesterview /> },
        { path: ":testId/:action", element: <ToolkitTesterview /> },
      ],
    },
    // Toolkit routes end
    {
      path: "embedViewer",
      element: <EmbedViewerLayout />,
      children: [
        { element: <EmbedViewer />, index: true },
        { path: "tree", element: <EmbedViewerTreeTask /> },
        { path: "cardsort", element: <EmbedViewerCardSort /> },
      ],
    },
    { path: "simply-feedback/:testId", element: <BrowserTest /> },
    { path: "/browser-test/:testId", element: <BrowserTestRedirect /> },
    { path: "preview/:testId", element: <TestPreview /> },
    { path: "share/report/:testKey", element: <TestReport /> },
    /**
     * The anonymous Files & Artifacts view.
     *
     * Top level on purpose, beside the shared-report routes: that keeps it outside `AuthGuard`,
     * which is also what stops `GlobalAppShell` and its sidebar from rendering around a page
     * whose viewer has no workspace.
     */
    { path: "share/project/:token", element: <PublicFilesView /> },
    { path: "/test/:draftId/heatmap", element: <Heatmap /> },
    { path: "/test/:draftId/heatmap/:taskId", element: <Heatmap /> },
    { path: "/share/:testKey/navtree/:taskId", element: <NavigationTree /> },
    { path: "/share/heatmap/:testKey", element: <Heatmap /> },
    { path: "/share/heatmap/:testKey/:taskId", element: <Heatmap /> },
    { path: "/ai-interview/:key", element: <AiInterviewLanding /> },
    { path: "/moderated/event/book/:eventId", element: <ModeratedSlotBooking /> },
    { path: "/moderated/event/booking/:slotId", element: <ModeratedSlotBooking showDetails /> },
    { path: "/moderated/event/booking/:slotId/cancel", element: <ModeratedSlotBooking isCancel /> },
    { path: "/moderated/event/booking/:slotId/reschedule", element: <ModeratedSlotBooking isReschedule /> },
    { path: "/share/report/:testKey/videoplayer/:userId/", element: <VideoPlayer /> },
    { path: "/share/report/:testKey/videoplayer/:userId/:taskId", element: <VideoPlayer /> },
    { path: "/share/report/:testKey/videoplayer/:userId/clip/:clipId", element: <VideoPlayer isSharedClip /> },
    { path: "/share/report/moderated/:testKey/videoplayer/:sessId", element: <ModeratedVideoPlayer isSharedPlayer /> },
    {
      path: "/share/report/moderated/:testKey/videoplayer/:sessId/clip/:clipId",
      element: <ModeratedVideoPlayer isSharedClip isSharedPlayer />,
    },
    { path: "*", element: <Error404 /> },
  ]);
}
